import { FC, ReactElement, Ref } from 'react';
import { forwardRef, useContext, useEffect, useMemo } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
    AppBar,
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    Slide,
    Toolbar,
    Typography,
} from '@mui/material';
import {
    Close as CloseIcon,
    FileDownload as FileDownloadIcon,
    SendOutlined as SendOutlinedIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import {
    ATTRIBUTION_EXPORT_METHOD_DOWNLOAD,
    ATTRIBUTION_EXPORT_METHOD_EMAIL,
    // ATTRIBUTION_EXPORT_TYPE_CSV,
    // ATTRIBUTION_EXPORT_TYPE_PDF,
    AttributionExport,
} from '../../../../types/AttributionExport';

import { InfoMessageContext } from '../../../../components/GlobalInfoMessage';
import { useHatDashboardPageContext } from '../../../../hooks/Pages/HatDashboardPage';
import ApiService from '../../../../ApiService';
import Column from '../../../../components/Column';
import HatExportLogo from './HatExportLogo';
import HatMetrics from '../HatMetrics';
import HatChannelByChannelPieChartCard from '../Charts/HatChannelByChannelPieChartCard';
import HatConversionByActionPieChartCard from '../Charts/HatConversionByActionPieChartCard';
import HatChannelTrafficTableView from '../Views/HatChannelTrafficTableView';
import HatConversionTrafficTableView from '../Views/HatConversionTrafficTableView';

import Row from '../../../../components/Row';
import DateRangeUtils from '../../../../utils/DateRangeUtils';

import { useHatExport } from '../../../../hooks/Exports/HatExport';
import { usePrintContext } from '../../../../hooks/usePrint';

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement;
    },
    ref: Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface HatExportDialogProps {
    onClose(): void;
    open: boolean;
    attributionExport: AttributionExport;
}

const HatExportDialog: FC<HatExportDialogProps> = (props: HatExportDialogProps): React.JSX.Element => {
    const { onClose, open, attributionExport } = props;

    const { showMessage } = useContext(InfoMessageContext);
    const { setPrint } = usePrintContext();
    const { hatCriteria } = useHatDashboardPageContext();
    const { generateExportFilename } = useHatExport();

    const exportAsPdf = async () => {
        let filename = await generateExportFilename('{id}.pdf');

        const toImageElement = document.getElementById('hat-export');

        if (toImageElement !== null) {
            html2canvas(toImageElement, { logging: false }).then(function (canvas) {
                const dataUrl = canvas.toDataURL('image/jpeg', 1.0);

                const pdf = new jsPDF();
                const imageProps = pdf.getImageProperties(dataUrl);

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imageProps.height * pdfWidth) / imageProps.width;
                const pdfImageScaleRatio = 0.9;

                pdf.addImage(dataUrl, 'JPEG', 10, 10, pdfWidth * pdfImageScaleRatio, pdfHeight * pdfImageScaleRatio);

                if (attributionExport.exportMethod === ATTRIBUTION_EXPORT_METHOD_EMAIL) {
                    const formData = new FormData();

                    // if (dealer) {
                    //     formData.append('dealerId', dealer.id as any);
                    // }

                    formData.append('exportMethod', attributionExport.exportMethod);
                    formData.append('exportType', attributionExport.exportType);

                    if (hatCriteria) {
                        if (hatCriteria.startDate) {
                            formData.append('startDate', hatCriteria.startDate.toISOString());
                        }
                        if (hatCriteria.endDate) {
                            formData.append('endDate', hatCriteria.endDate.toISOString());
                        }
                    }

                    formData.append('file', pdf.output('blob'), filename);

                    ApiService.processCampaignPerformanceExport(formData)
                        .then(() => {
                            showMessage('success', 'Your report has been emailed to you!');
                        })
                        .then(onClose);
                } else {
                    pdf.save(filename, {
                        returnPromise: true,
                    })
                        .then(() => {
                            showMessage('success', 'Downloading your report file.');
                        })
                        .then(onClose);
                }
            });
        }
    };

    const heading = useMemo(() => {
        let keywords: string[] = [];

        if (hatCriteria.startDate || hatCriteria.endDate) {
            keywords.push(DateRangeUtils.toLabel(hatCriteria.startDate, hatCriteria.endDate));
        }

        if (hatCriteria.previousStartDate || hatCriteria.previousEndDate) {
            keywords.push(DateRangeUtils.toLabel(hatCriteria.previousStartDate, hatCriteria.previousEndDate));
        }

        if (hatCriteria.mediaType) {
            keywords.push(hatCriteria.mediaType);
        }

        return keywords.join(' // ');
    }, [hatCriteria]);

    useEffect(() => {
        setPrint(open);

        if (open === true) {
            setTimeout(() => {
                // Trigger this to rerender Apex Charts
                window.dispatchEvent(new Event('resize'));
            }, 1000);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose} TransitionComponent={Transition}>
            <AppBar sx={{ position: 'relative', paddingRight: '0 !important' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                        sx={{ color: (theme) => theme.palette.action.active }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        {attributionExport.exportMethod === ATTRIBUTION_EXPORT_METHOD_DOWNLOAD && 'Export PDF'}
                        {attributionExport.exportMethod === ATTRIBUTION_EXPORT_METHOD_EMAIL && 'Email PDF'}
                    </Typography>
                    {attributionExport.exportMethod === ATTRIBUTION_EXPORT_METHOD_DOWNLOAD && (
                        <Button
                            autoFocus
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                exportAsPdf();
                            }}
                            startIcon={<FileDownloadIcon />}
                        >
                            Download PDF
                        </Button>
                    )}
                    {attributionExport.exportMethod === ATTRIBUTION_EXPORT_METHOD_EMAIL && (
                        <Button
                            autoFocus
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                exportAsPdf();
                            }}
                            startIcon={<SendOutlinedIcon />}
                        >
                            Send PDF via Email
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <DialogContent
                dividers
                sx={{
                    pt: 3,
                    pb: 5,
                    // maxWidth: 1250,
                }}
            >
                <Box id="hat-export">
                    <Column>
                        <Column justifyContent="center" alignItems="center">
                            <Row justifyContent="center" alignItems="center">
                                <Typography variant="h5">{heading}</Typography>
                            </Row>

                            {hatCriteria && <HatExportLogo criteria={hatCriteria} />}
                        </Column>

                        <Column>
                            <HatMetrics />
                        </Column>

                        <Column>
                            <Typography variant="h6">Visits</Typography>

                            <Grid container spacing={2}>
                                <Grid item md={6} xs={12}>
                                    <HatChannelByChannelPieChartCard />
                                </Grid>

                                <Grid item md={6} xs={12}>
                                    <HatChannelTrafficTableView />
                                </Grid>
                            </Grid>
                        </Column>

                        <Column>
                            <Typography variant="h6">Actions</Typography>

                            <Grid container spacing={2}>
                                <Grid item md={6} xs={12}>
                                    <HatConversionByActionPieChartCard />
                                </Grid>

                                <Grid item md={6} xs={12}>
                                    <HatConversionTrafficTableView />
                                </Grid>
                            </Grid>
                        </Column>
                    </Column>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export type { HatExportDialogProps };
export default HatExportDialog;
